import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { analysisService } from '../../services/analysisService';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faChevronRight, faFolderOpen, faTrash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { useState } from 'react';

export default function TextAnalysisList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Helper function to check if analysis needs glossary
  const checkNeedsGlossary = (analysis: any) => {
    // Check for glossary terms in analysisMetadata
    const hasGlossaryTerms = analysis.analysisMetadata?.glossaryTerms?.count > 0;
    const hasUniqueTerms = analysis.analysisMetadata?.glossaryTerms?.uniqueTerms?.length > 0;
    return hasGlossaryTerms || hasUniqueTerms;
  };

  const { data, isLoading } = useQuery({
    queryKey: ['grouped-analyses', 'MANUAL'],
    queryFn: () => analysisService.getGroupedAnalyses('MANUAL'),
  });

  const deleteMutation = useMutation({
    mutationFn: (analysisId: number) => analysisService.deleteAnalysis(analysisId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grouped-analyses', 'MANUAL'] });
      toast.success('Análisis eliminado correctamente');
      setDeletingId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al eliminar el análisis');
      setDeletingId(null);
    },
  });

  const handleDelete = (e: React.MouseEvent, analysisId: number, title: string) => {
    e.stopPropagation(); // Prevent navigation

    if (window.confirm(`¿Estás seguro de que quieres eliminar "${title}"?`)) {
      setDeletingId(analysisId);
      deleteMutation.mutate(analysisId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando análisis...</p>
        </div>
      </div>
    );
  }

  if (!data || data.analyses.length === 0) {
    return (
      <div className="text-center py-12">
        <FontAwesomeIcon icon={faFolderOpen} className="text-6xl text-gray-300 mb-4" />
        <p className="text-gray-500 text-lg">No hay análisis de texto</p>
        <p className="text-gray-400 text-sm mt-2">
          Escribe un texto para comenzar
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-700">
        Análisis de Texto ({data.total})
      </h3>
      <div className="grid gap-4">
        {data.analyses.map((textAnalysis) => (
          <Card
            key={textAnalysis.analysisId}
            className="hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate(`/analysis/${textAnalysis.analysisId}`)}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <FontAwesomeIcon icon={faPenToSquare} className="text-blue-600 text-2xl" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-900 text-lg">{textAnalysis.title}</h4>
                      <Badge className={`flex items-center gap-1.5 ${
                        textAnalysis.auditStatus === 'AUDITED'
                          ? 'bg-green-500/90 text-white border-green-400'
                          : 'bg-gray-400/90 text-white border-gray-300'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          textAnalysis.auditStatus === 'AUDITED' ? 'bg-green-200' : 'bg-gray-200'
                        }`}></span>
                        {textAnalysis.auditStatus === 'AUDITED' ? 'Auditado' : 'Sin Auditar'}
                      </Badge>
                    </div>

                    {/* Content Preview */}
                    {textAnalysis.originalText && (
                      <p className="text-xs text-gray-600 line-clamp-2 mb-3">
                        {textAnalysis.originalText.replace(/<[^>]*>/g, '').substring(0, 200)}...
                      </p>
                    )}

                    <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                      <span>{new Date(textAnalysis.createdAt).toLocaleDateString('es-ES')}</span>
                      {textAnalysis.sector && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <span>{textAnalysis.sector.icon}</span>
                            <span>{textAnalysis.sector.name}</span>
                          </span>
                        </>
                      )}
                    </div>

                    {/* Lenguaje Claro Compliance Summary */}
                    {textAnalysis.analysisMetadata?.b2Criteria && (() => {
                      const allCriteria = textAnalysis.analysisMetadata.b2Criteria.criteria || [];
                      const manualValidations = (textAnalysis.analysisMetadata as any)?.manualValidations || {};

                      // Organize criteria by UNE principle (EXACT same filters as AnalysisResultPage)
                      const pertinenciaCriteria = allCriteria.filter((c: any) =>
                        c.name.toLowerCase().includes('adecuación al público') ||
                        c.name.toLowerCase().includes('propósito claro') ||
                        c.name.toLowerCase().includes('suficiencia') ||
                        c.name.toLowerCase().includes('superflua')
                      );

                      const facilidadBusquedaCriteria = allCriteria.filter((c: any) =>
                        (c.name.toLowerCase().includes('estructura clara') && c.name.toLowerCase().includes('títulos')) ||
                        c.name.toLowerCase().includes('listas') ||
                        c.name.toLowerCase().includes('viñetas') ||
                        c.name.toLowerCase().includes('ilustraciones') ||
                        (c.name.toLowerCase().includes('gráficos') && c.name.toLowerCase().includes('iconos'))
                      );

                      const comprensibilidadCriteria = allCriteria.filter((c: any) => {
                        if (c.name.toLowerCase().includes('listas') ||
                            c.name.toLowerCase().includes('viñetas') ||
                            c.name.toLowerCase().includes('ilustraciones') ||
                            c.name.toLowerCase().includes('gráficos') ||
                            (c.name.toLowerCase().includes('iconos') && c.name.toLowerCase().includes('glosario'))) {
                          return false;
                        }

                        return (
                          ['legibilidad', 'vocabulario', 'claridad'].includes(c.category?.toLowerCase()) ||
                          (c.category?.toLowerCase() === 'estructura' &&
                           (c.name.toLowerCase().includes('voz activa') ||
                            c.name.toLowerCase().includes('conectores') ||
                            c.name.toLowerCase().includes('subordinadas'))) ||
                          (c.category?.toLowerCase() === 'formato' && c.name.toLowerCase().includes('mayúsculas')) ||
                          c.name.toLowerCase().includes('inflesz') ||
                          c.name.toLowerCase().includes('glosario') ||
                          c.name.toLowerCase().includes('negaciones') ||
                          c.name.toLowerCase().includes('indeterminadas') ||
                          c.name.toLowerCase().includes('tono y estilo')
                        );
                      });

                      const usabilidadCriteria = allCriteria.filter((c: any) =>
                        c.name.toLowerCase().includes('validación') ||
                        c.name.toLowerCase().includes('usuarios reales') ||
                        c.name.toLowerCase().includes('orientación a la acción') ||
                        c.name.toLowerCase().includes('siguientes pasos')
                      );

                      // Add 3 manual Pertinencia criteria with fixed indices
                      const manualPertinenciaCriteria = [
                        {
                          name: 'Adecuación al Público Objetivo',
                          category: 'pertinencia',
                          description: 'Verificar que el documento se ha escrito pensando en el perfil del lector',
                          scientific: false,
                          status: null,
                          index: 0
                        },
                        {
                          name: 'Propósito Claro',
                          category: 'pertinencia',
                          description: 'Evaluar si el documento responde directamente al objetivo del lector',
                          scientific: false,
                          status: null,
                          index: 1
                        },
                        {
                          name: 'Suficiencia de la Información',
                          category: 'pertinencia',
                          description: 'Verificar que contiene toda la información clave sin incluir contenido superfluo',
                          scientific: false,
                          status: null,
                          index: 2
                        }
                      ];

                      // Modified calculatePrincipleStatus to use LOCAL index (same as AnalysisResultPage)
                      const calculatePrincipleStatus = (criteria: any[], useWeights: boolean = false) => {
                        if (criteria.length === 0) return { passes: true, percentage: 100, validated: 0, total: 0 };

                        if (useWeights) {
                          let earnedScore = 0;
                          let maxScore = 0;

                          criteria.forEach((criterion, index) => {
                            // Use LOCAL index within this criteria array, NOT global index
                            const criterionKey = `${criterion.name}-${index}`;
                            const validation = manualValidations[criterionKey] !== undefined
                              ? manualValidations[criterionKey]
                              : (!criterion.scientific ? null : criterion.status === 'pass');

                            const weight = criterion.weight || 0;
                            maxScore += weight;

                            if (validation === true) {
                              earnedScore += weight;
                            }
                          });

                          const percentage = maxScore > 0 ? (earnedScore / maxScore) * 100 : 0;
                          return {
                            passes: percentage >= 85,
                            percentage: percentage,
                            validated: Math.round(earnedScore),
                            total: maxScore
                          };
                        } else {
                          let passedCount = 0;
                          let totalCriteria = criteria.length;

                          criteria.forEach((criterion, index) => {
                            // Use LOCAL index within this criteria array, NOT global index
                            const criterionKey = `${criterion.name}-${index}`;
                            const validation = manualValidations[criterionKey] !== undefined
                              ? manualValidations[criterionKey]
                              : (!criterion.scientific ? null : criterion.status === 'pass');

                            if (validation === true) {
                              passedCount++;
                            }
                          });

                          const percentage = (passedCount / totalCriteria) * 100;
                          return {
                            passes: percentage >= 85,
                            percentage: percentage,
                            validated: passedCount,
                            total: totalCriteria
                          };
                        }
                      };

                      // Calculate principle statuses exactly as in AnalysisResultPage
                      const pertinenciaStatus = calculatePrincipleStatus([...manualPertinenciaCriteria, ...pertinenciaCriteria], false);
                      const facilidadStatus = facilidadBusquedaCriteria.length > 0
                        ? calculatePrincipleStatus(facilidadBusquedaCriteria, false)
                        : { passes: true, percentage: 100, validated: 0, total: 0 };
                      const comprensibilidadStatus = calculatePrincipleStatus(comprensibilidadCriteria, true);
                      const usabilidadStatus = usabilidadCriteria.length > 0
                        ? calculatePrincipleStatus(usabilidadCriteria, false)
                        : { passes: true, percentage: 100, validated: 0, total: 0 };

                      const cumpleLenguajeClaro = pertinenciaStatus.passes && facilidadStatus.passes && comprensibilidadStatus.passes && usabilidadStatus.passes;

                      return (
                        <div className="space-y-3">
                          {/* First Row: Lenguaje Claro and Comprensibilidad B2 */}
                          <div className="flex items-center gap-4">
                            {/* Plain Language Compliance */}
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">Lenguaje Claro:</span>
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                cumpleLenguajeClaro
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {cumpleLenguajeClaro ? '✓ Cumple' : '✗ No cumple'}
                              </span>
                            </div>

                            {/* Comprensibilidad B2 */}
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">Comprensibilidad B2:</span>
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                comprensibilidadStatus.passes
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {comprensibilidadStatus.passes ? '✓ PASA' : '✗ NO PASA'}
                              </span>
                            </div>

                            {/* Glosario Badge - Same Row */}
                            {checkNeedsGlossary(textAnalysis) && (
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Glosario:</span>
                                <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-orange-100 text-orange-800 text-sm font-bold">
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-xs" />
                                  Requerido
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Second Row: Glossary Terms */}
                          {checkNeedsGlossary(textAnalysis) && (() => {
                            const glossaryTerms = textAnalysis.analysisMetadata?.glossaryTerms?.terms || [];

                            return glossaryTerms.length > 0 ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-gray-700">Términos que Requieren de Glosario:</span>
                                {glossaryTerms.map((term: string, index: number) => (
                                  <span key={index} className="px-2 py-1 rounded-md bg-orange-100 text-orange-800 text-sm font-medium">
                                    {term}
                                  </span>
                                ))}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleDelete(e, textAnalysis.analysisId, textAnalysis.title)}
                    disabled={deletingId === textAnalysis.analysisId}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  >
                    <FontAwesomeIcon
                      icon={faTrash}
                      className={deletingId === textAnalysis.analysisId ? 'animate-pulse' : ''}
                    />
                  </Button>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className="text-gray-400 text-xl"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
